"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LiquidityGaugemeisterWrapper = void 0;
const web3_js_1 = require("@solana/web3.js");
const utf8_1 = require("@project-serum/anchor/dist/cjs/utils/bytes/utf8");
const gauges_1 = require("../gauges");
const solana_contrib_1 = require("@saberhq/solana-contrib");
const bn_js_1 = __importDefault(require("bn.js"));
const quarry_sdk_1 = require("@quarryprotocol/quarry-sdk");
class LiquidityGaugemeisterWrapper {
    constructor(sdk, address) {
        this.sdk = sdk;
        this.address = address;
        this._data = null;
        this._gaugemeister = null;
    }
    get provider() {
        return this.sdk.provider;
    }
    get program() {
        return this.sdk.program;
    }
    static addressWithBump(sdk, gaugemeister) {
        return web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('liq-gamer'), gaugemeister.toBytes()], sdk.program.programId);
    }
    static async address(sdk, gaugemeister) {
        const [address] = await LiquidityGaugemeisterWrapper.addressWithBump(sdk, gaugemeister);
        return address;
    }
    static async fromGaugemeister(sdk, gaugemeister) {
        const gaugemeisterAddress = gaugemeister instanceof gauges_1.GaugemeisterWrapper
            ? gaugemeister.address
            : gaugemeister;
        const liquidityGaugemeister = new LiquidityGaugemeisterWrapper(sdk, await LiquidityGaugemeisterWrapper.address(sdk, gaugemeisterAddress));
        if (gaugemeister instanceof gauges_1.GaugemeisterWrapper) {
            liquidityGaugemeister._gaugemeister = gaugemeister;
        }
        return liquidityGaugemeister;
    }
    async reload() {
        this._data = await this.program.account.liquidityGaugemeister.fetch(this.address);
        if (!this._gaugemeister ||
            !this._gaugemeister.address.equals(this._data.gaugemeister)) {
            this._gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, this._data.gaugemeister);
        }
        return this._data;
    }
    async tryReload() {
        this._data = await this.program.account.liquidityGaugemeister.fetchNullable(this.address);
        if (this._data) {
            if (!this._gaugemeister ||
                !this._gaugemeister.address.equals(this._data.gaugemeister)) {
                this._gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, this._data.gaugemeister);
            }
        }
        return this._data;
    }
    async data() {
        if (!this._data) {
            return await this.reload();
        }
        return this._data;
    }
    async tryData() {
        if (!this._data) {
            return await this.tryReload();
        }
        return this._data;
    }
    async gaugemeister() {
        if (!this._gaugemeister) {
            await this.reload();
        }
        return this._gaugemeister;
    }
    async foremanWithBump(gaugemeister) {
        if (!gaugemeister) {
            gaugemeister = (await this.gaugemeister()).address;
        }
        return web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('foreman'), gaugemeister.toBytes()], this.program.programId);
    }
    async foreman(gaugemeister) {
        const [foreman] = await this.foremanWithBump(gaugemeister);
        return foreman;
    }
    static async foreman(sdk, gaugemeister) {
        return (await web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('foreman'), gaugemeister.toBytes()], sdk.program.programId))[0];
    }
    async shareAllocatorWithBump(gaugemeister) {
        if (!gaugemeister) {
            gaugemeister = (await this.gaugemeister()).address;
        }
        return web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('sharer'), gaugemeister.toBytes()], this.program.programId);
    }
    async shareAllocator(gaugemeister) {
        const [shareAloocator] = await this.shareAllocatorWithBump(gaugemeister);
        return shareAloocator;
    }
    async quarryAdminWithBump(gaugemeister) {
        if (!gaugemeister) {
            gaugemeister = (await this.gaugemeister()).address;
        }
        return web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('qadm'), gaugemeister.toBytes()], this.program.programId);
    }
    async quarryAdmin(gaugemeister) {
        const [quarryAdmin] = await this.quarryAdminWithBump(gaugemeister);
        return quarryAdmin;
    }
    static async quarryAdmin(sdk, gaugemeister) {
        return (await web3_js_1.PublicKey.findProgramAddress([(0, utf8_1.encode)('qadm'), gaugemeister.toBytes()], sdk.program.programId))[0];
    }
    async rewarder() {
        const data = await this.data();
        return await this.sdk.quarry.mine.loadRewarderWrapper(data.rewarder);
    }
    static async initialize({ sdk, gaugemeister, nextEpochStart, epochLength, rewarder, quarryOperator, rentPayer, admin, realm, }) {
        const wrapper = await LiquidityGaugemeisterWrapper.fromGaugemeister(sdk, gaugemeister);
        if (!gaugemeister || gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = await wrapper.gaugemeister();
        }
        if (!realm) {
            realm = (await gaugemeister.data()).realm;
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = await gaugemeister.realm();
        }
        if (!admin) {
            admin = (await realm.data()).admin;
        }
        const signers = [];
        if (!(admin instanceof web3_js_1.PublicKey)) {
            signers.push(admin);
            admin = admin.publicKey;
        }
        if (rentPayer && !(rentPayer instanceof web3_js_1.PublicKey)) {
            signers.push(rentPayer);
        }
        const tx = new solana_contrib_1.TransactionEnvelope(sdk.provider, [
            await sdk.program.methods
                .initializeLiquidityGaugemeister(nextEpochStart, epochLength)
                .accounts({
                liquidityGaugemeister: wrapper.address,
                gaugemeister: gaugemeister.address,
                foreman: await wrapper.foreman(),
                realm: realm.address,
                admin,
                rewarder,
                quarryOperator,
                quarryAdmin: await wrapper.quarryAdmin(),
                shareAllocator: await wrapper.shareAllocator(),
                rentPayer: rentPayer instanceof web3_js_1.PublicKey
                    ? rentPayer
                    : (rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || sdk.provider.walletKey,
                systemProgram: web3_js_1.SystemProgram.programId,
                escrowRelockerProgram: sdk.escrowRelocker.program.programId,
                quarryOperatorProgram: sdk.quarry.programs.Operator.programId,
            })
                .instruction(),
        ], signers);
        return {
            tx,
            wrapper,
        };
    }
    async createLiquidityGauge({ quarry, rentPayer, gaugemeister, rewarder, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!rewarder) {
            rewarder = (await this.data()).rewarder;
        }
        const gauge = await gauges_1.GaugeWrapper.address(gaugemeister, quarry.toBuffer());
        const signers = [];
        if (rentPayer && !(rentPayer instanceof web3_js_1.PublicKey)) {
            signers.push(rentPayer);
        }
        const tx = new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.sdk.program.methods
                .createLiquidityGauge()
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                foreman: await this.foreman(),
                rewarder,
                quarry,
                gauge,
                rentPayer: rentPayer instanceof web3_js_1.PublicKey
                    ? rentPayer
                    : (rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || this.sdk.provider.walletKey,
                systemProgram: web3_js_1.SystemProgram.programId,
                escrowRelockerProgram: this.sdk.escrowRelocker.program.programId,
            })
                .instruction(),
        ], signers);
        return {
            tx,
            gauge,
        };
    }
    async updateGaugeSnapshot({ gauge, quarry, gaugemeister, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!gauge) {
            if (!quarry) {
                throw new Error('One of quarry or guage must be set');
            }
            gauge = await gauges_1.GaugeWrapper.address(gaugemeister, quarry.toBuffer());
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.sdk.program.methods
                .updateGaugeSnapshot()
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                foreman: await this.foreman(),
                gauge,
                escrowRelockerProgram: this.sdk.escrowRelocker.program.programId,
            })
                .instruction(),
        ]);
    }
    async updateQuarry({ gauge, quarry, gaugemeister, rewarder, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!gauge) {
            if (!quarry) {
                throw new Error('One of quarry or guage must be set');
            }
            gauge = await gauges_1.GaugeWrapper.address(gaugemeister, quarry.toBuffer());
        }
        if (!quarry) {
            const gaugeWrapper = new gauges_1.GaugeWrapper(this.sdk.escrowRelocker, gauge);
            const info = (await gaugeWrapper.data()).info;
            quarry = new web3_js_1.PublicKey(info.subarray(0, 32));
        }
        if (!rewarder) {
            rewarder = (await this.data()).rewarder;
        }
        let rewarderData;
        if (rewarder instanceof quarry_sdk_1.RewarderWrapper) {
            rewarderData = rewarder.rewarderData;
        }
        else {
            rewarderData = await this.sdk.quarry.programs.Mine.account.rewarder.fetch(rewarder);
        }
        if (!(rewarder instanceof web3_js_1.PublicKey)) {
            rewarder = rewarder.rewarderKey;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .updateQuarry()
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                foreman: await this.foreman(),
                gauge,
                quarry,
                rewarder,
                quarryOperator: rewarderData.authority,
                shareAllocator: await this.shareAllocator(),
                escrowRelockerProgram: this.sdk.escrowRelocker.program.programId,
                quarryMineProgram: this.sdk.quarry.programs.Mine.programId,
                quarryOperatorProgram: this.sdk.quarry.programs.Operator.programId,
            })
                .instruction(),
        ]);
    }
    async setupNewEpoch({ gaugemeister, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .setupNewEpoch()
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                foreman: await this.foreman(),
                escrowRelockerProgram: this.sdk.escrowRelocker.program.programId,
            })
                .instruction(),
        ]);
    }
    async setEpochLength({ length, admin, gaugemeister, realm, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!realm) {
            realm = (await gaugemeister.data()).realm;
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = await gaugemeister.realm();
        }
        if (!admin) {
            admin = (await realm.data()).admin;
        }
        const signers = [];
        if (!(admin instanceof web3_js_1.PublicKey)) {
            signers.push(admin);
            admin = admin.publicKey;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .setEpochLength(length)
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                realm: realm.address,
                admin,
            })
                .instruction(),
        ], signers);
    }
    async enableGauge({ gauge, admin, gaugemeister, realm, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!realm) {
            realm = (await gaugemeister.data()).realm;
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = await gaugemeister.realm();
        }
        if (!admin) {
            admin = (await realm.data()).admin;
        }
        const signers = [];
        if (!(admin instanceof web3_js_1.PublicKey)) {
            signers.push(admin);
            admin = admin.publicKey;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .gaugeEnable()
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                realm: realm.address,
                foreman: await this.foreman(),
                admin,
                gauge,
                escrowRelockerProgram: this.sdk.escrowRelocker.program.programId,
            })
                .instruction(),
        ], signers);
    }
    async disableGauge({ gauge, admin, gaugemeister, realm, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!realm) {
            realm = (await gaugemeister.data()).realm;
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = await gaugemeister.realm();
        }
        if (!admin) {
            admin = (await realm.data()).admin;
        }
        const signers = [];
        if (!(admin instanceof web3_js_1.PublicKey)) {
            signers.push(admin);
            admin = admin.publicKey;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .gaugeDisable()
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                realm: realm.address,
                foreman: await this.foreman(),
                admin,
                gauge,
                escrowRelockerProgram: this.sdk.escrowRelocker.program.programId,
            })
                .instruction(),
        ], signers);
    }
    async setRateSetter({ rateSetter, admin, gaugemeister, realm, rewarder, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!realm) {
            realm = (await gaugemeister.data()).realm;
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = await gaugemeister.realm();
        }
        if (!rewarder) {
            rewarder = (await this.data()).rewarder;
        }
        let rewarderData;
        if (rewarder instanceof quarry_sdk_1.RewarderWrapper) {
            rewarderData = rewarder.rewarderData;
        }
        else {
            rewarderData = await this.sdk.quarry.programs.Mine.account.rewarder.fetch(rewarder);
        }
        if (!(rewarder instanceof web3_js_1.PublicKey)) {
            rewarder = rewarder.rewarderKey;
        }
        if (!admin) {
            admin = (await realm.data()).admin;
        }
        const signers = [];
        if (!(admin instanceof web3_js_1.PublicKey)) {
            signers.push(admin);
            admin = admin.publicKey;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.sdk.program.methods
                .setOperatorRateSetter()
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                realm: realm.address,
                foreman: await this.foreman(),
                admin,
                rewarder,
                quarryOperator: rewarderData.authority,
                quarryAdmin: await this.quarryAdmin(),
                delegate: rateSetter,
                quarryOperatorProgram: this.sdk.quarry.programs.Operator.programId,
            })
                .instruction(),
        ], signers);
    }
    async setQuarryCreator({ quarryCreator, admin, gaugemeister, realm, rewarder, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!realm) {
            realm = (await gaugemeister.data()).realm;
        }
        if (realm instanceof web3_js_1.PublicKey) {
            realm = await gaugemeister.realm();
        }
        if (!rewarder) {
            rewarder = (await this.data()).rewarder;
        }
        let rewarderData;
        if (rewarder instanceof quarry_sdk_1.RewarderWrapper) {
            rewarderData = rewarder.rewarderData;
        }
        else {
            rewarderData = await this.sdk.quarry.programs.Mine.account.rewarder.fetch(rewarder);
        }
        if (!(rewarder instanceof web3_js_1.PublicKey)) {
            rewarder = rewarder.rewarderKey;
        }
        if (!admin) {
            admin = (await realm.data()).admin;
        }
        const signers = [];
        if (!(admin instanceof web3_js_1.PublicKey)) {
            signers.push(admin);
            admin = admin.publicKey;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.sdk.program.methods
                .setOperatorQuarryCreator()
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                realm: realm.address,
                foreman: await this.foreman(),
                admin,
                rewarder,
                quarryOperator: rewarderData.authority,
                quarryAdmin: await this.quarryAdmin(),
                delegate: quarryCreator,
                quarryOperatorProgram: this.sdk.quarry.programs.Operator.programId,
            })
                .instruction(),
        ], signers);
    }
    async forcedCloseLiquidityVote({ gaugeVote, admin, rentCollector, gaugemeister, realm, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!realm) {
            realm = (await gaugemeister.data()).realm;
        }
        if (!(realm instanceof web3_js_1.PublicKey)) {
            realm = realm.address;
        }
        if (!admin) {
            admin = await gaugemeister.admin();
        }
        const signers = [];
        if (!(admin instanceof web3_js_1.PublicKey)) {
            signers.push(admin);
            admin = admin.publicKey;
        }
        const nftOwners = (await this.provider.connection.getTokenLargestAccounts((await gaugeVote.gaugeVoter.escrow.data()).nftMint)).value;
        if (nftOwners.length === 0 || nftOwners[0].amount === '0') {
            return await gaugeVote.closeDead({
                rentCollector: rentCollector || this.provider.walletKey,
            });
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .forcedCloseLiquidityVote()
                .accounts({
                gaugeVote: gaugeVote.address,
                escrow: gaugeVote.gaugeVoter.escrow.address,
                nftToken: nftOwners[0].address,
                rentCollector: rentCollector || this.provider.walletKey,
                gaugeVoter: gaugeVote.gaugeVoter.address,
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                realm,
                foreman: await this.foreman(),
                admin,
                gauge: gaugeVote.gauge.address,
                escrowRelockerProgram: this.sdk.escrowRelocker.program.programId,
            })
                .instruction(),
        ], signers);
    }
    async close({ newForeman = this.sdk.provider.walletKey, newQuarryAdmin = this.sdk.provider.walletKey, admin, rentCollector, gaugemeister, realm, rewarder, }) {
        if (!gaugemeister) {
            gaugemeister = await this.gaugemeister();
        }
        if (gaugemeister instanceof web3_js_1.PublicKey) {
            gaugemeister = new gauges_1.GaugemeisterWrapper(this.sdk.escrowRelocker, gaugemeister);
        }
        if (!realm) {
            realm = (await gaugemeister.data()).realm;
        }
        if (!(realm instanceof web3_js_1.PublicKey)) {
            realm = realm.address;
        }
        if (!admin) {
            admin = await gaugemeister.admin();
        }
        const signers = [];
        if (!(admin instanceof web3_js_1.PublicKey)) {
            signers.push(admin);
            admin = admin.publicKey;
        }
        if (!rewarder) {
            rewarder = (await this.data()).rewarder;
        }
        let rewarderData;
        if (rewarder instanceof quarry_sdk_1.RewarderWrapper) {
            rewarderData = rewarder.rewarderData;
        }
        else {
            rewarderData = await this.sdk.quarry.programs.Mine.account.rewarder.fetch(rewarder);
        }
        if (!(rewarder instanceof web3_js_1.PublicKey)) {
            rewarder = rewarder.rewarderKey;
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .closeLiquidityGaugemeister(newForeman)
                .accounts({
                liquidityGaugemeister: this.address,
                gaugemeister: gaugemeister.address,
                foreman: await this.foreman(),
                realm,
                admin,
                rewarder,
                quarryOperator: rewarderData.authority,
                quarryAdmin: await this.quarryAdmin(),
                newQuarryAdmin,
                rentCollector: rentCollector || this.provider.walletKey,
                escrowRelockerProgram: this.sdk.escrowRelocker.program.programId,
                quarryOperatorProgram: this.sdk.quarry.programs.Operator.programId,
            })
                .instruction(),
        ], signers);
    }
    async stage(time) {
        const data = await this.data();
        const gaugemeisterData = await (await this.gaugemeister()).data();
        if (!time) {
            time = new bn_js_1.default((await this.provider.connection.getParsedAccountInfo(web3_js_1.SYSVAR_CLOCK_PUBKEY)).value.data.parsed.info.unixTimestamp);
        }
        let stage;
        if (time.lt(gaugemeisterData.snapshotTime)) {
            if (data.gaugeSnapshotsUpdated.lt(gaugemeisterData.gaugeCount)) {
                stage = 'updating_snapshots';
            }
            else {
                stage = 'voting';
            }
        }
        else {
            if (data.gaugeSnapshotsUpdated.lt(gaugemeisterData.gaugeCount)) {
                stage = 'starting_next_epoch';
            }
            else if (data.quarriesUpdated.lt(gaugemeisterData.gaugeCount)) {
                stage = 'updating_quarries';
            }
            else {
                stage = 'starting_next_epoch';
            }
        }
        return {
            stage,
            time,
        };
    }
}
exports.LiquidityGaugemeisterWrapper = LiquidityGaugemeisterWrapper;
//# sourceMappingURL=LiquidityGaugemeister.js.map